import { useState, FormEvent, ChangeEvent } from 'react';
import Breadcrumb from '../components/sections/Breadcrumb';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitMessage({ 
        type: 'success', 
        text: 'Thank you for your message! We\'ll get back to you soon.' 
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setSubmitMessage({ 
        type: 'error', 
        text: 'Something went wrong. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Breadcrumb title="Contact Us" subtitle="GET IN TOUCH" />

      {/* Hero Section */}
      <section 
        className="py-20 bg-white"
        style={{
          padding: '5rem 0',
          backgroundColor: '#ffffff'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-4xl mx-auto text-center"
            style={{
              maxWidth: '56rem',
              margin: '0 auto',
              textAlign: 'center'
            }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
              style={{
                fontSize: 'clamp(1.875rem, 3vw, 2.25rem)',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '1.5rem',
                lineHeight: '1.2'
              }}
            >
              We'd Love to Hear From You
            </h2>
            <p 
              className="text-xl text-gray-600 leading-relaxed"
              style={{
                fontSize: '1.25rem',
                color: '#4B5563',
                lineHeight: '1.75'
              }}
            >
              Have questions about the Tanzania Digital Awards? Want to learn more about nominations
              or partnerships? Get in touch with us today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        className="py-20 bg-gray-50"
        style={{
          padding: '5rem 0',
          backgroundColor: '#F9FAFB'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-6xl mx-auto"
            style={{
              maxWidth: '72rem',
              margin: '0 auto'
            }}
          >
            <div 
              className="grid grid-cols-1 lg:grid-cols-2 gap-12"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '3rem'
              }}
            >
              {/* Contact Form */}
              <div 
                className="bg-white rounded-2xl shadow-xl p-8"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '1rem',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  padding: '2rem'
                }}
              >
                <h3 
                  className="text-2xl font-bold text-gray-900 mb-6"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '1.5rem'
                  }}
                >
                  Send Us a Message
                </h3>
                
                {submitMessage && (
                  <div 
                    className={`p-4 rounded-lg mb-6 ${
                      submitMessage.type === 'success' 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                    style={{
                      padding: '1rem',
                      borderRadius: '0.5rem',
                      marginBottom: '1.5rem',
                      backgroundColor: submitMessage.type === 'success' ? '#F0FDF4' : '#FEF2F2',
                      color: submitMessage.type === 'success' ? '#166534' : '#991B1B',
                      border: `1px solid ${submitMessage.type === 'success' ? '#DCFCE7' : '#FEE2E2'}`
                    }}
                  >
                    {submitMessage.text}
                  </div>
                )}

                <form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.5rem'
                  }}
                >
                  <div>
                    <label 
                      htmlFor="name" 
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label 
                      htmlFor="email" 
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label 
                      htmlFor="phone" 
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      placeholder="+255 XXX XXX XXX"
                    />
                  </div>

                  <div>
                    <label 
                      htmlFor="subject" 
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}
                    >
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label 
                      htmlFor="message" 
                      className="block text-sm font-semibold text-gray-700 mb-2"
                      style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        resize: 'none',
                        minHeight: '150px'
                      }}
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="contact-submit-btn w-full px-8 py-4 bg-orange-500 text-white rounded-lg font-semibold text-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      width: '100%',
                      padding: '1rem 2rem',
                      backgroundColor: '#ffba17',
                      color: 'white',
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                      fontSize: '1.125rem',
                      transition: 'background-color 0.3s ease',
                      opacity: isSubmitting ? '0.5' : '1',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      marginTop: '1rem',
                      display: 'block !important',
                      border: 'none'
                    }}
                  >
                    {isSubmitting ? (
                      <div 
                        className="flex items-center justify-center"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <div 
                          className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"
                          style={{
                            animation: 'spin 1s linear infinite',
                            height: '1.25rem',
                            width: '1.25rem',
                            borderBottomWidth: '2px',
                            borderColor: 'white',
                            borderRadius: '50%',
                            marginRight: '0.75rem'
                          }}
                        ></div>
                        Sending...
                      </div>
                    ) : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div 
                className="space-y-8"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2rem'
                }}
              >
                <div>
                  <h3 
                    className="text-2xl font-bold text-gray-900 mb-6"
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '1.5rem'
                    }}
                  >
                    Contact Information
                  </h3>
                  <p 
                    className="text-gray-600 leading-relaxed mb-8"
                    style={{
                      color: '#4B5563',
                      lineHeight: '1.8',
                      marginBottom: '2rem'
                    }}
                  >
                    Feel free to reach out to us through any of the following channels.
                    We're here to help and answer any questions you may have.
                  </p>
                </div>

                {/* Contact Cards */}
                <div 
                  className="space-y-6"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.5rem'
                  }}
                >
                  {/* Address */}
                  <div 
                    className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-lg"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      padding: '1.5rem',
                      backgroundColor: 'white',
                      borderRadius: '0.75rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <div 
                      className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        width: '3rem',
                        height: '3rem',
                        backgroundColor: '#ffba17',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <i 
                        className="fa-solid fa-location-dot text-white text-xl"
                        style={{
                          color: 'white',
                          fontSize: '1.25rem'
                        }}
                      ></i>
                    </div>
                    <div>
                      <h4 
                        className="text-lg font-semibold text-gray-900 mb-2"
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#111827',
                          marginBottom: '0.5rem'
                        }}
                      >
                        Office Address
                      </h4>
                      <p 
                        className="text-gray-600 leading-relaxed"
                        style={{
                          color: '#4B5563',
                          lineHeight: '1.75'
                        }}
                      >
                        8th Floor, Victoria House, Mikocheni<br />
                        Dar es Salaam, Tanzania
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div 
                    className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-lg"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      padding: '1.5rem',
                      backgroundColor: 'white',
                      borderRadius: '0.75rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <div 
                      className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        width: '3rem',
                        height: '3rem',
                        backgroundColor: '#2563EB',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <i 
                        className="fa-solid fa-envelope text-white text-xl"
                        style={{
                          color: 'white',
                          fontSize: '1.25rem'
                        }}
                      ></i>
                    </div>
                    <div>
                      <h4 
                        className="text-lg font-semibold text-gray-900 mb-2"
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#111827',
                          marginBottom: '0.5rem'
                        }}
                      >
                        Email Address
                      </h4>
                      <a 
                        href="mailto:info@digitalawards.co.tz"
                        className="text-gray-600 hover:text-orange-500 transition-colors"
                        style={{
                          color: '#4B5563',
                          transition: 'color 0.3s ease',
                          textDecoration: 'none'
                        }}
                      >
                        info@digitalawards.co.tz
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div 
                    className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-lg"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      padding: '1.5rem',
                      backgroundColor: 'white',
                      borderRadius: '0.75rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <div 
                      className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        width: '3rem',
                        height: '3rem',
                        backgroundColor: '#059669',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <i 
                        className="fa-solid fa-phone text-white text-xl"
                        style={{
                          color: 'white',
                          fontSize: '1.25rem'
                        }}
                      ></i>
                    </div>
                    <div>
                      <h4 
                        className="text-lg font-semibold text-gray-900 mb-2"
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#111827',
                          marginBottom: '0.5rem'
                        }}
                      >
                        Phone Numbers
                      </h4>
                      <div 
                        className="space-y-1"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.25rem'
                        }}
                      >
                        <a 
                          href="tel:+255737957082"
                          className="block text-gray-600 hover:text-orange-500 transition-colors"
                          style={{
                            display: 'block',
                            color: '#4B5563',
                            transition: 'color 0.3s ease',
                            textDecoration: 'none'
                          }}
                        >
                          +255 737 957082
                        </a>
                        <a 
                          href="tel:+255719579733"
                          className="block text-gray-600 hover:text-orange-500 transition-colors"
                          style={{
                            display: 'block',
                            color: '#4B5563',
                            transition: 'color 0.3s ease',
                            textDecoration: 'none'
                          }}
                        >
                          +255 719 579733
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Social Media */}
                  <div 
                    className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-lg"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      padding: '1.5rem',
                      backgroundColor: 'white',
                      borderRadius: '0.75rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <div 
                      className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        width: '3rem',
                        height: '3rem',
                        backgroundColor: '#7C3AED',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <i 
                        className="fa-solid fa-share-nodes text-white text-xl"
                        style={{
                          color: 'white',
                          fontSize: '1.25rem'
                        }}
                      ></i>
                    </div>
                    <div>
                      <h4 
                        className="text-lg font-semibold text-gray-900 mb-3"
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#111827',
                          marginBottom: '0.75rem'
                        }}
                      >
                        Follow Us
                      </h4>
                      <div 
                        className="flex space-x-3"
                        style={{
                          display: 'flex',
                          gap: '0.75rem'
                        }}
                      >
                        <a 
                          href="https://www.facebook.com/digitalawardstz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-orange-500 hover:text-white transition-all"
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <i className="fa-brands fa-facebook-f"></i>
                        </a>
                        <a 
                          href="https://x.com/digitalawardstz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-orange-500 hover:text-white transition-all"
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <i className="fa-brands fa-x-twitter"></i>
                        </a>
                        <a 
                          href="https://www.instagram.com/digitalawardstz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-orange-500 hover:text-white transition-all"
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <i className="fa-brands fa-instagram"></i>
                        </a>
                        <a 
                          href="https://www.youtube.com/digitalawardstz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-orange-500 hover:text-white transition-all"
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <i className="fa-brands fa-youtube"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section 
        className="h-96 relative"
        style={{
          height: '24rem',
          position: 'relative'
        }}
      >
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3961.7447429408416!2d39.2710339!3d-6.7933121!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c4c1c1d6e0a6d%3A0x5a7f1c25dd9c2e9c!2sMikocheni%2C%20Dar%20es%20Salaam%2C%20Tanzania!5e0!3m2!1sen!2sus!4v1698176239457!5m2!1sen!2sus" 
          width="100%" 
          height="100%" 
          style={{
            border: 'none',
            width: '100%',
            height: '100%'
          }}
          allowFullScreen 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          title="Tanzania Digital Awards Office Location"
        ></iframe>
      </section>
    </div>
  );
};

export default Contact;